import React, { useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import alephLogo from '../assets/logos/Aleph-Logomark-Blue.png';
import alephLogoWhite from '../assets/logos/Aleph-Logomark-White.png';
import ProfileIcon from './icons/ProfileIcon';
import MyBooksIcon from './icons/MyBooksIcon';
import LibraryIcon from './icons/LibraryIcon';
import ExploreIcon from './icons/ExploreIcon';
import HeaderSearchResults, { headerSearchOptionId } from './HeaderSearchResults';
import SearchClearButton from './SearchClearButton';
import { usePlayerVisibility } from '@/contexts/PlayerVisibilityContext';
import { useHeaderSearchResults } from '@/hooks/useHeaderSearchResults';
import { Book, getBookUrl, titleToSlug } from '@/utils/bookRouting';

interface TopNavigationProps {
  isDarkMode: boolean;
  onToggleMenu: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateToProfile?: () => void;
  onLibrarySearch?: (query: string) => void;
  /** Opens a book picked from the search results; falls back to URL routing. */
  onBookSelect?: (bookSlug: string, source?: string) => void;
  showProfileIcon?: boolean;
  isLoading?: boolean;
  isHomePage?: boolean;
  /**
   * Keep the collapsed burger layout up to 2xl instead of lg. The reader
   * shares its header row with the book, so its nav needs the extra width.
   */
  compactNav?: boolean;
  /** The primary nav item for the current page, marked with an underline. */
  activeItem?: NavItem;
  className?: string;
}

type NavItem = 'explore' | 'library' | 'myBooks' | 'profile';

const ActiveUnderline: React.FC = () => (
  <span
    aria-hidden="true"
    className="pointer-events-none absolute inset-x-4 -bottom-0 h-px rounded-full bg-current"
  />
);

const TopNavigation: React.FC<TopNavigationProps> = ({
  isDarkMode,
  onToggleMenu,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToSignup,
  onNavigateToExplore,
  onNavigateToProfile,
  onLibrarySearch,
  onBookSelect,
  onNavigateToMyBooks,
  onNavigateToLibrary,
  showProfileIcon = false,
  isLoading = false,
  isHomePage,
  compactNav = false,
  activeItem,
  className,
}) => {
  const { isPersistentPlayerOpen } = usePlayerVisibility();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const desktopSearchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [activeResultIndex, setActiveResultIndex] = useState(-1);
  const listboxIdBase = useId();
  const desktopListboxId = `${listboxIdBase}-desktop`;
  const mobileListboxId = `${listboxIdBase}-mobile`;
  const {
    results: searchResults,
    total: searchResultTotal,
    resultsQuery,
    isLoading: searchLoading,
    error: searchError,
    flush: flushSearch,
  } = useHeaderSearchResults(searchQuery, isSearchOpen);
  const trimmedSearchQuery = searchQuery.trim();
  const searchPending = searchLoading || resultsQuery !== trimmedSearchQuery;
  const showSearchResults = isSearchOpen && trimmedSearchQuery.length > 0;

  useEffect(() => {
    setActiveResultIndex(-1);
  }, [searchResults]);

  // The desktop and phone-width inputs are both mounted; CSS shows one.
  const getVisibleSearchInput = () => {
    const mobileInput = mobileSearchInputRef.current;
    return mobileInput && mobileInput.offsetParent !== null ? mobileInput : desktopSearchInputRef.current;
  };

  useEffect(() => {
    if (!isSearchOpen) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => getVisibleSearchInput()?.focus());

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isSearchOpen]);

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  const handleSelectResult = (book: Book) => {
    const bookSlug = book.slug || titleToSlug(book.title);

    if (onBookSelect) {
      onBookSelect(bookSlug, 'top_nav_search');
    } else {
      window.history.pushState({}, '', getBookUrl(bookSlug));
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    handleCloseSearch();
  };

  const handleSeeAllResults = () => {
    if (!trimmedSearchQuery) {
      return;
    }

    if (onLibrarySearch) {
      onLibrarySearch(trimmedSearchQuery);
    } else {
      const nextUrl = `/library?q=${encodeURIComponent(trimmedSearchQuery)}`;
      window.history.pushState({}, '', nextUrl);
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    handleCloseSearch();
  };

  // Enter opens the highlighted result; otherwise it just searches now
  // rather than waiting out the debounce. Results never navigate on their own.
  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const activeResult = searchPending ? undefined : searchResults[activeResultIndex];
    if (activeResult) {
      handleSelectResult(activeResult);
      return;
    }

    flushSearch();
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      handleCloseSearch();
      return;
    }

    if (searchResults.length === 0 || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) {
      return;
    }

    event.preventDefault();
    const step = event.key === 'ArrowDown' ? 1 : -1;
    setActiveResultIndex((index) => {
      if (index === -1) {
        return step === 1 ? 0 : searchResults.length - 1;
      }
      return (index + step + searchResults.length) % searchResults.length;
    });
  };

  const getComboboxProps = (listboxId: string) => ({
    role: 'combobox' as const,
    'aria-autocomplete': 'list' as const,
    'aria-expanded': showSearchResults,
    'aria-controls': listboxId,
    'aria-activedescendant':
      activeResultIndex >= 0 ? headerSearchOptionId(listboxId, activeResultIndex) : undefined,
  });

  const renderSearchResults = (listboxId: string, className: string) =>
    showSearchResults ? (
      <HeaderSearchResults
        id={listboxId}
        query={trimmedSearchQuery}
        results={searchResults}
        total={searchResultTotal}
        isPending={searchPending}
        error={searchError}
        activeIndex={activeResultIndex}
        isDarkMode={isDarkMode}
        onSelect={handleSelectResult}
        onSeeAll={handleSeeAllResults}
        onActiveIndexChange={setActiveResultIndex}
        className={className}
      />
    ) : null;

  const handleToggleSearch = () => {
    if (isSearchOpen) {
      handleCloseSearch();
      return;
    }

    // Commit the input while the click is still an active user gesture so
    // mobile browsers permit focus and open the on-screen keyboard.
    flushSync(() => {
      setIsSearchOpen(true);
    });

    getVisibleSearchInput()?.focus();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    getVisibleSearchInput()?.focus();
  };

  // Written out in full so Tailwind can see both variants at build time.
  const navItemClasses = compactNav ? 'hidden 2xl:flex' : 'hidden lg:flex';
  // The page footer (Terms/Privacy/theme toggle/etc.) is `hidden lg:block`,
  // so it's normally reachable only above that breakpoint — but the docked
  // persistent player sits fixed over it at every width, so the burger menu
  // (which duplicates the footer's links) needs to stay reachable instead of
  // collapsing into the desktop nav once the player is open.
  const burgerClasses = isPersistentPlayerOpen ? 'flex' : compactNav ? '2xl:hidden' : 'lg:hidden';
  // Logged-in Library/My Books stay visible at every width, collapsing to
  // icon-only below the breakpoint; they only make way for the open search.
  const primaryItemClasses = isSearchOpen ? navItemClasses : 'flex';
  // Explore leaves the phone-width row (it stays in the burger menu) and
  // returns from xs (512px) up, icon-only until its label shows.
  const exploreItemClasses = isSearchOpen ? navItemClasses : 'hidden xs:flex';
  const primaryTextClasses = compactNav ? 'hidden 2xl:inline' : 'hidden lg:inline';
  // Icon-only buttons share one fixed square so they size and space evenly
  // whatever their icon's size; they widen to fit once the label shows.
  const primarySizeClasses = compactNav ? 'w-10 2xl:w-auto 2xl:px-3' : 'w-10 lg:w-auto lg:px-3';
  // Profile moves to the top of the burger menu whenever the burger is shown.
  const profileItemClasses = isPersistentPlayerOpen ? 'hidden' : navItemClasses;

  return (
    <header
      role="banner"
      data-search-open={isSearchOpen || undefined}
      className={`fixed top-0 left-0 w-full z-50 border-b top-nav-shadow pt-safe px-safe ${className ?? ''} ${
        isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
      } ${
        isHomePage ? 'border-none' : (isDarkMode ? 'border-gray-600' : 'border-gray-200')
      }`}
      style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}>
      <div ref={searchContainerRef}>
      <nav role="navigation" aria-label="Primary navigation" className="flex items-center justify-between h-17 px-2 xs:px-4">
      {/* Aleph Logo */}
      <button
        onClick={onNavigateHome}
        className="flex shrink-0 items-center cursor-pointer hover:opacity-80 transition-opacity"
      >
        <img
          src={isDarkMode ? alephLogoWhite : alephLogo}
          alt="Aleph Logo"
          className="w-11 h-11 xs:w-12 xs:h-12 object-contain mx-3"
        />
        {/* The logged-in icon row doesn't fit beside the wordmark under 372px. */}
        <span className={`text-2xl xs:text-3xl font-semibold ${showProfileIcon ? 'max-[372px]:hidden' : ''} ${
          isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'
        }`}>Aleph</span>
      </button>

      {/* Right side controls */}
      <div className="flex items-center space-x-1 xs:space-x-2">
        {/* Authentication Buttons */}
        <div className="flex items-center space-x-1 xs:space-x-2">
          {isLoading ? (
            // Render a placeholder or nothing while loading to prevent flicker
            <div className="w-40 h-9" /> // Adjust size to match buttons
          ) : showProfileIcon ? (
            <>
              <div
                className={`relative flex items-center rounded-full ${
                  isSearchOpen
                    ? isDarkMode
                      ? 'bg-black-1500'
                      : 'bg-true-white'
                    : ''
                }`}
              >
                <button
                  type="button"
                  onClick={handleToggleSearch}
                  aria-label={isSearchOpen ? 'Close library search' : 'Open library search'}
                  className={`relative z-10 flex w-10 h-10 items-center justify-center rounded-full transition-colors cursor-pointer ${
                    isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200/50'
                  }`}
                  title="Search library"
                >
                  <svg className="w-[24px] h-[24px]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M21 21L16.65 16.65"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="11"
                      cy="11"
                      r="6"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                  </svg>
                </button>
                {isSearchOpen ? (
                  <form onSubmit={handleSearchSubmit} className="hidden xs:flex items-center pl-1 pr-2.5">
                    <input
                      ref={desktopSearchInputRef}
                      type="search"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      aria-label="Search library"
                      {...getComboboxProps(desktopListboxId)}
                      placeholder="Search by title or author"
                      className={`w-28 ${searchQuery ? 'xs:w-46' : 'xs:w-52'} border-0 bg-transparent py-2 pr-2 text-base outline-none ${
                        isDarkMode
                          ? 'text-theme-primary-dark placeholder:text-black-300'
                          : 'text-theme-primary-light placeholder:text-blue-400'
                      }`}
                    />
                    <SearchClearButton visible={searchQuery.length > 0} onClear={handleClearSearch} className="h-6 w-6" />
                  </form>
                ) : null}
                {renderSearchResults(
                  desktopListboxId,
                  'absolute right-0 top-full z-20 mt-2 hidden w-[min(26rem,calc(100vw-5rem))] xs:block',
                )}
              </div>
              <button
                onClick={onNavigateToExplore}
                aria-label="Go to explore page"
                aria-current={activeItem === 'explore' ? 'page' : undefined}
                className={`${exploreItemClasses} ${primarySizeClasses} relative h-10 items-center justify-center gap-2 rounded-full transition-colors cursor-pointer ${
                  isDarkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-200/50'
                }`}
                title="Explore"
              >
                <ExploreIcon className="w-6 h-6" size={28} />
                <span className={`${primaryTextClasses} text-[16px] font-medium`}>Explore</span>
                {activeItem === 'explore' && <ActiveUnderline />}
              </button>
              <button
                onClick={onNavigateToLibrary}
                aria-label="Go to library page"
                aria-current={activeItem === 'library' ? 'page' : undefined}
                className={`${primaryItemClasses} ${primarySizeClasses} relative h-10 items-center justify-center gap-2 rounded-full transition-colors cursor-pointer ${
                  isDarkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-200/50'
                }`}
                title="Library"
              >
                <LibraryIcon className="w-5 h-5 ml-0.5 mr-[0.75px]" size={24} />
                <span className={`${primaryTextClasses} text-[16px] font-medium`}>Library</span>
                {activeItem === 'library' && <ActiveUnderline />}
              </button>
              <button
                onClick={onNavigateToMyBooks}
                aria-label="Go to my books page"
                aria-current={activeItem === 'myBooks' ? 'page' : undefined}
                className={`${primaryItemClasses} ${primarySizeClasses} relative h-10 items-center justify-center gap-2 rounded-full transition-colors cursor-pointer ${
                  isDarkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-200/50'
                }`}
                title="My Books"
              >
                <MyBooksIcon className="w-5 h-5" size={24} />
                <span className={`${primaryTextClasses} text-[16px] font-medium`}>My Books</span>
                {activeItem === 'myBooks' && <ActiveUnderline />}
              </button>
              <button
                onClick={onNavigateToProfile}
                aria-label="Go to profile page"
                aria-current={activeItem === 'profile' ? 'page' : undefined}
                className={`${profileItemClasses} relative w-10 h-10 items-center justify-center rounded-full transition-colors cursor-pointer ${
                  isDarkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-200/50'
                }`}
                title="Profile"
              >
                <ProfileIcon className="w-[22px] h-[22px]" size={24} aria-hidden="true" />
                {activeItem === 'profile' && <ActiveUnderline />}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onNavigateToExplore}
                aria-label="Go to explore page"
                aria-current={activeItem === 'explore' ? 'page' : undefined}
                className={`${navItemClasses} relative h-10 items-center gap-2 px-3 rounded-full transition-colors cursor-pointer ${
                  isDarkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-200/50'
                }`}
                title="Explore"
              >
                <ExploreIcon className="w-6 h-6" size={28} />
                <span className="text-[16px] font-medium">Explore</span>
                {activeItem === 'explore' && <ActiveUnderline />}
              </button>
              <button
                onClick={onNavigateToLibrary}
                aria-label="Go to library page"
                aria-current={activeItem === 'library' ? 'page' : undefined}
                className={`${navItemClasses} relative h-10 items-center gap-2 px-3 rounded-full transition-colors cursor-pointer ${
                  isDarkMode
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-200/50'
                }`}
                title="Library"
              >
                <LibraryIcon className="w-5 h-5 ml-0.5 mr-[0.75px]" size={24} />
                <span className="text-[16px] font-medium">Library</span>
                {activeItem === 'library' && <ActiveUnderline />}
              </button>
              <button
                onClick={() => onNavigateToLogin?.('login_button')}
                className={`py-1.5 2xs:py-2 px-3 2xs:px-4 text-sm font-medium whitespace-nowrap rounded-full border transition-colors cursor-pointer ${
                  isDarkMode
                    ? 'border-blue-400 text-blue-400 hover:bg-blue-900/20'
                    : 'border-blue-600 text-blue-600 hover:bg-blue-50'
                }`}
              >
                Log In
              </button>
              <button
                onClick={() => onNavigateToSignup?.('signup_button')}
                className={`py-1.5 2xs:py-2 px-3 2xs:px-4 text-sm font-medium whitespace-nowrap rounded-full border transition-colors cursor-pointer ${
                  isDarkMode
                    ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                    : 'border-blue-700 bg-blue-700 hover:bg-blue-800 text-white'
                }`}
              >
                Sign Up
              </button>
            </>
          )}
        </div>

        {/* Burger Menu - Mobile Only */}
        <button
          onClick={onToggleMenu}
          aria-label="Open navigation menu"
          className={`p-2 rounded-lg transition-colors cursor-pointer ${burgerClasses} ${
            isDarkMode
              ? 'hover:bg-black-700'
              : 'hover:bg-black-100'
          }`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      </nav>
      {isSearchOpen ? (
        <div className="relative px-2 pb-2 xs:hidden">
          <form
            onSubmit={handleSearchSubmit}
            className={`flex items-center rounded-full border px-4 py-2 ${
              isDarkMode
                ? 'border-black-800 bg-black-1500'
                : 'border-black-200 bg-true-white'
            }`}
          >
            <input
              ref={mobileSearchInputRef}
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search library"
              {...getComboboxProps(mobileListboxId)}
              placeholder="Search by title or author"
              className={`w-full border-0 bg-transparent text-base outline-none ${
                isDarkMode
                  ? 'text-theme-primary-dark placeholder:text-black-300'
                  : 'text-theme-primary-light placeholder:text-blue-400'
              }`}
            />
            <SearchClearButton visible={searchQuery.length > 0} onClear={handleClearSearch} className="ml-2 h-6 w-6" />
          </form>
          {/* Float below the header so it keeps its height while results show. */}
          {renderSearchResults(mobileListboxId, 'absolute inset-x-2 top-full z-20')}
        </div>
      ) : null}
      </div>
    </header>
  );
};

export default TopNavigation;
